import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { UserAction } from '~/eds/src/types/UserAction';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { Option } from './first-step/FirstStep';

export enum SetupStep {
  FirstStep = 1,
  Template = 2,
  Form = 3,
}

export type FlowPath = 'template' | 'manual' | null;

export const useSetupWizard = (initialStep = SetupStep.FirstStep) => {
  const intl = useIntl();
  const isPlaybookTemplatesEnabled = useFlag(FlagType.PlaybookTemplates);
  const [step, setStep] = useState(initialStep);
  const [flowPath, setFlowPath] = useState<FlowPath>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  const navigateToFirstStep = () => setStep(SetupStep.FirstStep);
  const navigateToTemplate = () => setStep(SetupStep.Template);
  const navigateToForm = () => setStep(SetupStep.Form);

  const handleOptionSelect = (option: Option) => {
    switch (option) {
      case Option.Template:
        if (isPlaybookTemplatesEnabled) {
          setFlowPath('template');
          navigateToTemplate();
        }
        break;
      case Option.Manual:
        setFlowPath('manual');
        navigateToForm();
        break;
      case Option.Ai:
        // Future: handle AI flow
        break;
    }
  };

  const breadcrumbs = useMemo((): UserAction[] | null => {
    const createPlaybookLabel = intl.formatMessage({
      id: 'playbooks.Setup.createPlaybookTitle',
      defaultMessage: 'Create Playbook',
      description: 'Title for the create playbook modal',
    });
    const createWithTemplateLabel = intl.formatMessage({
      id: 'playbooks.Setup.breadcrumbCreateWithTemplate',
      defaultMessage: 'Create with a Template',
      description: 'Breadcrumb text for the template selection step',
    });
    const reviewPlaybookLabel = intl.formatMessage({
      id: 'playbooks.Setup.breadcrumbReviewPlaybook',
      defaultMessage: 'Review Playbook',
      description: 'Breadcrumb text for the review playbook step',
    });
    const inputGuidelinesLabel = intl.formatMessage({
      id: 'playbooks.Setup.breadcrumbInputGuidelines',
      defaultMessage: 'Input Guidelines Manually',
      description: 'Breadcrumb text for the manual guidelines step',
    });

    const backToFirstStep = {
      text: createPlaybookLabel,
      onClick: navigateToFirstStep,
    };

    switch (step) {
      case SetupStep.Template:
        return [backToFirstStep, { text: createWithTemplateLabel }];

      case SetupStep.Form:
        if (flowPath === 'template') {
          return [
            backToFirstStep,
            { text: createWithTemplateLabel, onClick: navigateToTemplate },
            { text: reviewPlaybookLabel },
          ];
        }
        if (flowPath === 'manual') {
          return [backToFirstStep, { text: inputGuidelinesLabel }];
        }
        return null;

      default:
        return null;
    }
  }, [step, flowPath, intl]);

  return {
    step,
    flowPath,
    selectedTemplateId,
    setSelectedTemplateId,
    breadcrumbs,
    handleOptionSelect,
    navigateToForm,
  };
};
